import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faList, faUserGraduate } from "@fortawesome/free-solid-svg-icons";
import Filter from "./StudentFilter";
import StudentMiniDashboard from "./StudentMiniDashboard";
import Pagination from "../../../components/common/Pagination";
import StudentFormModal from "./StudentFormModal";
import StudentImportModal from "./StudentImportModal";
import StudentDetailModal from "./StudentDetailModal";
import {
  getAllStudents,
  getStudentStats,
  deleteStudent,
  exportStudentsCSV,
} from "../../../api/elearning/students/students";
import {
  GraduationCap,
  BookOpen,
  FolderOpen,
  Trash2,
  Edit3,
  Sparkles,
  CircleHelp,
} from "lucide-react";
import { hasPermission } from "../../../utils/permissionUtils";
import { showSuccessToast, showErrorToast } from "../../../components/ConfirmDialog";
import { confirmDelete, DELETE_ENTITY } from "../../../utils/confirmDelete";
import { startStudentsPageTour } from "./studentsPageTour";

const EN_SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const StudentListPage = () => {
  const [students, setStudents] = useState([]);
  const [filters, setFilters] = useState({
    keyword: "",
    category_id: "",
    classify: "",
    is_active: "",
    level: "",
    no_class: "",
  });
  const [showDrawer, setShowDrawer] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // State để quản lý hiển thị modal import
  const [showImport, setShowImport] = useState(false);

  // ✅ State cho chức năng chi tiết học sinh
  const [showDetail, setShowDetail] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // ✅ Chọn nhiều để xóa hàng loạt
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const canQuickTour = hasPermission("elearning.students", "R");

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const s = String(dateString).trim();
    const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:$|[T\s])/);
    if (iso) {
      const day = iso[3].padStart(2, "0");
      const month = EN_SHORT_MONTHS[Number(iso[2]) - 1] || iso[2].padStart(2, "0");
      return `${day}-${month}-${iso[1]}`;
    }
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return s;
    const day = String(date.getDate()).padStart(2, "0");
    const monthShort = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();
    return `${day}-${monthShort}-${year}`;
  };

  const GENDER_LABELS = {
    male: "Nam",
    female: "Nữ",
  };

  // ✅ Bổ sung state để quản lý mode cho FormModal
  const [formMode, setFormMode] = useState("create");

  const fetchStudents = async (
    f = filters,
    page = currentPage,
    size = pageSize
  ) => {
    try {
      const query = { ...f, page, size };
      const data = await getAllStudents(query);
      // Giả sử API trả về { data: [], total: 0 } hoặc trực tiếp mảng
      const records = data.records || (Array.isArray(data) ? data : []);
      setStudents(records);
      setTotalRecords(data.total || records.length);
    } catch (err) {
      console.error("Lỗi khi tải danh sách học viên:", err);
    }
  };

  const fetchStats = async (f = filters) => {
    setStatsLoading(true);
    try {
      const data = await getStudentStats(f);
      setStats(data);
    } catch (err) {
      console.error("Lỗi khi tải thống kê học viên:", err);
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  useEffect(() => {
    fetchStats(filters);
  }, [
    filters.keyword,
    filters.category_id,
    filters.classify,
    filters.is_active,
    filters.level,
  ]);

  const handleView = (student) => {
    setSelectedStudent(student);
    setShowDetail(true);
  };

  // ✅ Hàm mở Form ở chế độ Thêm mới
  const handleAddNew = () => {
    setFormMode("create");
    setSelectedStudent(null);
    setShowDrawer(true);
  };

  // ✅ Hàm xử lý Edit từ Chi tiết hoặc RowAction
  const handleEdit = (student) => {
    setFormMode("edit");
    setSelectedStudent(student);
    setShowDetail(false); // Đóng detail trước khi mở form
    setShowDrawer(true);
  };

  const handleDeleteStudent = async (student) => {
    const confirm = await confirmDelete({
      type: DELETE_ENTITY.student,
      name: student?.full_name || student?.name,
    });
    if (!confirm.isConfirmed) return;
    try {
      await deleteStudent(student.id);
      showSuccessToast("Đã xóa học viên");
      if (selectedStudent?.id === student.id) {
        setShowDetail(false);
        setSelectedStudent(null);
      }
      setSelectedStudentIds((prev) => prev.filter((id) => id !== student.id));
      fetchStudents(filters, currentPage, pageSize);
      fetchStats(filters);
    } catch (err) {
      showErrorToast(err?.message || "Không thể xóa học viên");
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
    setSelectedStudentIds([]);
    fetchStudents(newFilters, 1, pageSize);
  };

  const downloadStudentsCsv = async (exportFilters, filePrefix) => {
    setExporting(true);
    try {
      const blob = await exportStudentsCSV(exportFilters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${filePrefix}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      showSuccessToast("Đã tải file CSV");
    } catch (err) {
      const msg =
        err?.response?.status === 404
          ? "Không có học viên phù hợp để xuất"
          : "Không thể xuất file CSV";
      showErrorToast(msg);
    } finally {
      setExporting(false);
    }
  };

  const handleExportCsv = () => downloadStudentsCsv(filters, "hoc_vien_export");

  const handleExportNoClassCsv = () =>
    downloadStudentsCsv({ ...filters, no_class: "true" }, "hoc_vien_chua_co_lop");

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedStudentIds([]);
    fetchStudents(filters, page, pageSize);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
    setSelectedStudentIds([]);
    fetchStudents(filters, 1, size);
  };

  const canDeleteStudent = hasPermission("elearning.students", "D");
  const canUpdateStudent = hasPermission("elearning.students", "U");
  const showActionsColumn = canUpdateStudent || canDeleteStudent;

  const stickyHead = "sticky z-30 bg-gray-50/95";
  const stickyBody = "sticky z-20 bg-white group-hover:bg-red-50/30";

  const visibleStudentIds = Array.isArray(students)
    ? students.map((s) => s.id).filter(Boolean)
    : [];
  const selectedSet = new Set(selectedStudentIds);
  const allVisibleSelected =
    canDeleteStudent &&
    visibleStudentIds.length > 0 &&
    visibleStudentIds.every((id) => selectedSet.has(id));

  const toggleSelectOne = (studentId) => {
    if (!studentId) return;
    setSelectedStudentIds((prev) => {
      const exists = prev.includes(studentId);
      if (exists) return prev.filter((id) => id !== studentId);
      return [...prev, studentId];
    });
  };

  const toggleSelectAllVisible = () => {
    if (!canDeleteStudent) return;
    setSelectedStudentIds((prev) => {
      const prevSet = new Set(prev);
      if (allVisibleSelected) {
        return prev.filter((id) => !visibleStudentIds.includes(id));
      }
      const next = [...prev];
      visibleStudentIds.forEach((id) => {
        if (!prevSet.has(id)) next.push(id);
      });
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (!canDeleteStudent) return;
    if (!selectedStudentIds.length) return;

    const ids = [...selectedStudentIds];
    const confirm = await confirmDelete({
      type: DELETE_ENTITY.student_bulk,
      context: { count: ids.length },
    });
    if (!confirm.isConfirmed) return;

    setBulkDeleting(true);
    try {
      const settled = await Promise.allSettled(
        ids.map(async (id) => deleteStudent(id))
      );

      const okCount = settled.filter((r) => r.status === "fulfilled").length;
      const failed = settled
        .filter((r) => r.status === "rejected")
        .map((r, idx) => ({ idx, reason: r.reason }));

      showSuccessToast(`Đã xóa ${okCount}/${ids.length} học viên`);

      if (failed.length > 0) {
        showErrorToast(
          "Một vài học viên không thể xóa. Kiểm tra lại dữ liệu/ràng buộc DB."
        );
      }

      if (selectedStudent?.id && ids.includes(selectedStudent.id)) {
        setShowDetail(false);
        setSelectedStudent(null);
      }

      setSelectedStudentIds([]);
      fetchStudents(filters, currentPage, pageSize);
      fetchStats(filters);
    } catch (e) {
      showErrorToast(e?.message || "Không thể xóa hàng loạt học viên");
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleQuickTour = () => {
    window.setTimeout(() => startStudentsPageTour(), 80);
  };

  const openGuide = () => {
    const guideUrl = `${window.location.origin}/guides/elearning-students-guide.html`;
    window.open(guideUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="p-4">
      {/* HEADER */}
      <div
        className="flex items-center justify-between mb-8"
        data-tour="students-header"
      >
        <div className="flex items-center gap-4">
          {/* Icon Wrapper với Shadow Red */}
          <div className="bg-red-700 p-2.5 rounded-2xl text-white shadow-lg shadow-red-200">
            <FontAwesomeIcon
              icon={faUserGraduate}
              className="text-xl w-6 h-6 flex items-center justify-center"
            />
          </div>

          <div>
            {/* Title với style font-black và uppercase đồng bộ */}
            <h1 className="text-xl font-black text-gray-800 tracking-tight uppercase">
              Quản lý <span className="text-red-700">học viên</span>
            </h1>

            {/* Subtitle với hiệu ứng pulse và font tracking-widest */}
            <div className="flex items-center gap-2 mt-0.5">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              <p className="text-[11px] text-gray-500 font-bold uppercase tracking-wider text-nowrap">
                <span className="text-red-600">{students.length}</span> học viên
                đang hiển thị
              </p>
            </div>
          </div>
        </div>
        {canQuickTour ? (
          <div className="flex items-center gap-2" data-tour="students-header-guides">
            <HeaderIconButton icon={Sparkles} label="Hướng dẫn nhanh" onClick={handleQuickTour} />
            <HeaderIconButton icon={CircleHelp} label="Hướng dẫn sử dụng" onClick={openGuide} />
          </div>
        ) : null}
      </div>

      <div data-tour="students-dashboard">
        <StudentMiniDashboard
          stats={stats || {}}
          loading={statsLoading}
          exporting={exporting}
          onExportNoClass={
            (stats?.no_class_count || 0) > 0 ? handleExportNoClassCsv : undefined
          }
        />
      </div>

      <div data-tour="students-filter">
        <Filter
          onNewClick={handleAddNew}
          onImportClick={() => setShowImport(true)}
          onFilterChange={handleFilterChange}
          onExportClick={handleExportCsv}
          exporting={exporting}
        />
      </div>

      {canDeleteStudent && selectedStudentIds.length > 0 && (
        <div className="flex justify-end mb-3">
          <button
            type="button"
            disabled={bulkDeleting}
            onClick={handleBulkDelete}
            className="px-4 py-2 rounded-xl bg-white border border-red-200 text-red-600 font-bold hover:bg-red-50 transition-all disabled:opacity-60"
          >
            {bulkDeleting
              ? "Đang xóa..."
              : `Xóa hàng loạt (${selectedStudentIds.length})`}
          </button>
        </div>
      )}

      {/* TABLE */}
      <div
        className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
        data-tour="students-table"
      >
        <div className="overflow-x-auto">
          <table className="w-full table-auto border-collapse">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-100 text-left">
                <th
                  className={`p-4 w-[50px] min-w-[50px] text-center text-xs font-bold text-gray-400 uppercase tracking-wider ${stickyHead} left-0`}
                >
                  #
                </th>
                {canDeleteStudent ? (
                  <th
                    className={`p-4 w-[52px] min-w-[52px] text-center text-xs font-bold text-gray-400 uppercase tracking-wider ${stickyHead} left-[50px]`}
                  >
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleSelectAllVisible()}
                      className="w-4 h-4 accent-red-600"
                      aria-label="Chọn tất cả trên trang"
                    />
                  </th>
                ) : null}
                <th
                  className={`p-4 min-w-[200px] text-xs font-bold text-gray-400 uppercase tracking-wider ${
                    canDeleteStudent
                      ? `${stickyHead} left-[102px] border-r-2 border-gray-100`
                      : `${stickyHead} left-[50px] border-r-2 border-gray-100`
                  }`}
                >
                  Thông tin học viên
                </th>
                <th
                  className={`p-4 min-w-[180px] text-xs font-bold text-gray-400 uppercase tracking-wider ${
                    canDeleteStudent
                      ? ""
                      : `${stickyHead} left-[250px] border-r-2 border-gray-100`
                  }`}
                >
                  Đơn vị
                </th>
                <th className="p-4 min-w-[220px] text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Khóa học & Lớp
                </th>
                <th className="p-4 min-w-[120px] text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Phân loại
                </th>
                <th className="p-4 min-w-[140px] text-center text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Trạng thái
                </th>
                {showActionsColumn ? (
                  <th
                    className={`p-4 w-[100px] min-w-[100px] text-center text-xs font-bold text-gray-400 uppercase tracking-wider ${stickyHead} right-0 border-l-2 border-gray-100`}
                  >
                    Thao tác
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {students.map((student, index) => (
                <tr
                  key={student.id}
                  onClick={() => handleView(student)}
                  className="hover:bg-red-50/30 transition-colors group cursor-pointer"
                >
                  {/* 1. STT */}
                  <td
                    className={`p-4 text-sm text-gray-400 text-center font-medium italic ${stickyBody} left-0`}
                  >
                    {(currentPage - 1) * pageSize + index + 1}
                  </td>

                  {canDeleteStudent ? (
                    <td className={`p-4 text-center align-middle ${stickyBody} left-[50px]`}>
                      <input
                        type="checkbox"
                        checked={selectedSet.has(student.id)}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleSelectOne(student.id)}
                        className="w-4 h-4 accent-red-600"
                        aria-label={`Chọn học viên ${student.full_name || student.id}`}
                      />
                    </td>
                  ) : null}

                  {/* 2. THÔNG TIN CÁ NHÂN */}
                  <td
                    className={`p-4 ${
                      canDeleteStudent
                        ? `${stickyBody} left-[102px] border-r-2 border-gray-100`
                        : `${stickyBody} left-[50px] border-r-2 border-gray-100`
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-red-100 text-red-600 flex items-center justify-center font-bold text-sm shrink-0 border border-white">
                        {student.full_name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-bold text-gray-900 group-hover:text-red-600 transition-colors capitalize text-sm">
                          {student.full_name?.toLowerCase()}
                        </div>
                        <div className="mt-0.5 text-[10px] text-gray-400 font-medium">
                          {`Mã HS: ${student.student_code || "—"}`}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 text-[10px] text-gray-500 font-medium italic">
                          <span>
                            {GENDER_LABELS[student.gender] || student.gender}
                          </span>
                          <span>•</span>
                          <span>{formatDate(student.dob)}</span>
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* 3. ĐƠN VỊ */}
                  <td
                    className={`p-4 ${
                      canDeleteStudent ? "" : `${stickyBody} left-[250px] border-r-2 border-gray-100`
                    }`}
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5 text-[12px] font-bold text-gray-700">
                        <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
                        <span
                          className="truncate max-w-[150px]"
                          title={student.branch_name}
                        >
                          {student.branch_name || "N/A"}
                        </span>
                      </div>
                      {(student.classify === "truong-hoc" ||
                        student.classify === "school") && (
                        <div className="flex items-center gap-1.5 text-[11px] text-gray-500 font-medium">
                          <GraduationCap size={12} className="text-gray-400" />
                          <span
                            className="truncate max-w-[150px]"
                            title={student.school_name}
                          >
                            {student.school_name}
                          </span>
                        </div>
                      )}
                    </div>
                  </td>

                  {/* 4. KHÓA HỌC & LỚP */}
                  <td className="p-4">
                    <div className="flex flex-wrap gap-1.5">
                      {student.classes?.length > 0 ? (
                        student.classes.map((cls, idx) => (
                          <div
                            key={idx}
                            className="flex flex-col border border-gray-100 rounded overflow-hidden shadow-sm"
                          >
                            <div className="bg-gray-50 px-2 py-0.5 text-[9px] font-bold text-gray-500 border-b border-gray-100">
                              {cls.course_name}
                            </div>
                            <div className="bg-white px-2 py-0.5 text-[10px] font-bold text-red-600 flex items-center gap-1">
                              <BookOpen size={10} />
                              {cls.class_name}
                            </div>
                          </div>
                        ))
                      ) : (
                        <span className="text-[10px] text-gray-400 italic font-medium">
                          Chưa có lớp
                        </span>
                      )}
                    </div>
                  </td>

                  {/* 5. PHÂN LOẠI */}
                  <td className="p-4">
                    {student.classify === "trung-tam" ||
                    student.classify === "center" ? (
                      <span className="px-2 py-1 rounded-full bg-blue-50 text-blue-600 text-[10px] font-bold uppercase border border-blue-100">
                        Trung tâm
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded-full bg-orange-50 text-orange-600 text-[10px] font-bold uppercase border border-orange-100">
                        Trường học
                      </span>
                    )}
                  </td>

                  {/* 6. TRẠNG THÁI */}
                  <td className="p-4 text-center">
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border
                      ${
                        student.status
                          ? "bg-green-50 text-green-700 border-green-100"
                          : "bg-gray-50 text-gray-500 border-gray-100"
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                          student.status
                            ? "bg-green-500 animate-pulse"
                            : "bg-gray-400"
                        }`}
                      />
                      {student.status ? "Hoạt động" : "Tạm ngưng"}
                    </span>
                  </td>
                  {showActionsColumn ? (
                    <td
                      className={`p-4 text-center align-middle ${stickyBody} right-0 border-l-2 border-gray-100`}
                    >
                      <div className="inline-flex items-center justify-center gap-1">
                        {canUpdateStudent ? (
                          <button
                            type="button"
                            title="Sửa học viên"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(student);
                            }}
                            className="inline-flex items-center justify-center p-2 rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                          >
                            <Edit3 size={17} />
                          </button>
                        ) : null}
                        {canDeleteStudent ? (
                          <button
                            type="button"
                            title="Xóa học viên"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteStudent(student);
                            }}
                            className="inline-flex items-center justify-center p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <Trash2 size={17} />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
              {students.length === 0 && (
                <tr>
                  <td
                    colSpan={6 + (canDeleteStudent ? 1 : 0) + (showActionsColumn ? 1 : 0)}
                    className="p-20 text-center"
                  >
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-4">
                        <FolderOpen size={32} className="text-gray-200" />
                      </div>
                      <p className="text-gray-400 font-medium text-sm">
                        Hiện chưa có dữ liệu học viên nào.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* PAGINATION SECTION */}
      {totalRecords > 0 && (
        <Pagination
          totalRecords={totalRecords}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}

      {/* MODAL THÊM MỚI */}
      <StudentFormModal
        isOpen={showDrawer}
        mode={formMode}
        editingStudent={formMode === "edit" ? selectedStudent : null}
        onClose={() => setShowDrawer(false)}
        onSaved={() => {
          setSelectedStudentIds([]);
          fetchStudents(filters, currentPage, pageSize);
          fetchStats(filters);
          setShowDrawer(false);
        }}
      />

      {/* MODAL IMPORT FILE */}
      <StudentImportModal
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        onRefresh={() => {
          setSelectedStudentIds([]);
          fetchStudents(filters, currentPage, pageSize);
          fetchStats(filters);
        }}
      />

      {/* MODAL CHI TIẾT (Drawer trượt từ phải sang) */}
      <StudentDetailModal
        isOpen={showDetail}
        student={selectedStudent}
        onClose={() => setShowDetail(false)}
        onEdit={(student) => {
          handleEdit(student);
        }}
        onDelete={canDeleteStudent ? handleDeleteStudent : undefined}
      />
    </div>
  );
};

export default StudentListPage;

function HeaderIconButton({ icon: Icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="group relative inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-red-200 bg-white text-red-700 shadow-sm transition-all hover:bg-red-50 hover:border-red-300"
    >
      <Icon className="h-5 w-5" strokeWidth={2.2} />
      <span className="pointer-events-none absolute bottom-full right-0 mb-2 rounded-lg border border-red-100 bg-white px-2.5 py-1 text-[11px] font-bold text-red-700 whitespace-nowrap opacity-0 translate-y-1 shadow-md transition-all group-hover:opacity-100 group-hover:translate-y-0">
        {label}
      </span>
    </button>
  );
}
